import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, CheckCircle2, CalendarCheck } from 'lucide-react';
import { PROGRAMS } from '../../data/programs';
import { submitContact } from '../../lib/api';
import { withTimeout } from '../../lib/utils';

const SUBMIT_TIMEOUT_MS = 4000;
const GOLD = '#C99B2D';

const FIELD_CLASSES =
  'w-full bg-[#EBEBEB] rounded-md px-4 text-sm font-semibold text-[#1F1F1F] placeholder-[#4A4A4A] focus:outline-none focus:ring-2 focus:ring-[#C99B2D]/60';

export default function BookDemoModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', course: '', why: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const contactPromise = submitContact({
      name: form.name,
      email: form.email,
      phone: form.phone,
      course: form.course,
      message: `Free demo request — ${form.course}${form.why ? ` | Why: ${form.why}` : ''}`,
      source: 'website-demo',
      page: `demo_popup:${window.location.pathname}`,
    });

    const result = await withTimeout(contactPromise, SUBMIT_TIMEOUT_MS);
    setLoading(false);
    if (!result.timedOut && result.error) {
      setError('Something went wrong. Please try again.');
      return;
    }
    setSubmitted(true);
  };

  // Portal to <body> so ancestors with backdrop-filter/transform can't trap
  // this fixed overlay (same issue GetInTouchModal hit inside the navbar).
  return createPortal(
    <div
      data-testid="book-demo-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F1F3D]/70 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          data-testid="book-demo-modal-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F0F0F0] hover:bg-[#E0E0E0] flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-[#585858]" />
        </button>

        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: '#F7EBCE' }}>
              <CheckCircle2 className="w-7 h-7" style={{ color: GOLD }} />
            </div>
            <h3 className="font-heading font-bold text-xl text-[#1F1F1F]">
              You're in{form.name ? `, ${form.name}` : ''}!
            </h3>
            <p className="font-body text-sm text-[#585858] mt-2">
              Our team will send you the free demo session link for {form.course} shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-7 sm:p-8 space-y-4">
            <div className="text-center mb-6">
              <div
                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3"
                style={{ backgroundColor: '#F7EBCE', color: GOLD }}
              >
                <CalendarCheck className="w-3.5 h-3.5" />
                Free Live Session
              </div>
              <h2 className="font-heading font-black text-3xl text-[#1F1F1F]">Book a Free Demo</h2>
            </div>

            <input
              data-testid="book-demo-name-input"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Name"
              className={`${FIELD_CLASSES} h-12`}
            />
            <input
              data-testid="book-demo-email-input"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              required
              placeholder="Email"
              className={`${FIELD_CLASSES} h-12`}
            />
            <input
              data-testid="book-demo-phone-input"
              type="tel"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              required
              placeholder="Number"
              className={`${FIELD_CLASSES} h-12`}
            />
            <div className="relative">
              <select
                data-testid="book-demo-course-select"
                value={form.course}
                onChange={e => setForm({ ...form, course: e.target.value })}
                required
                className={`${FIELD_CLASSES} h-12 appearance-none pr-10 ${form.course ? '' : 'text-[#4A4A4A]'}`}
              >
                <option value="" disabled>Choose a Course</option>
                {PROGRAMS.map(p => (
                  <option key={p.slug} value={p.title}>{p.title}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              <ChevronDown className="w-4 h-4 text-[#4A4A4A] absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <textarea
              data-testid="book-demo-why-input"
              value={form.why}
              onChange={e => setForm({ ...form, why: e.target.value })}
              rows={3}
              placeholder="Why You want to Learn the Course?"
              className={`${FIELD_CLASSES} py-3 resize-none`}
            />

            {error && <p className="text-xs text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              data-testid="book-demo-submit-btn"
              className="w-full font-heading font-bold text-lg text-white py-3.5 rounded-full transition-colors disabled:opacity-60"
              style={{ backgroundColor: GOLD }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#B3892A'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = GOLD; }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
            <p className="text-[11px] text-center text-[#9AA5B5]">
              By submitting, you agree to be contacted about the demo session.
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
